import {
  ETF_METHODOLOGIES,
  ETF_SORT_KEYS,
  type EtfMethodology,
  type EtfScreenerQuery,
  type EtfSortKey,
  type EtfSortOrder,
} from "../../../api/services/etf-screener.type";

/**
 * Filter model for the ETF screener. UI values equal the API values, so the
 * translation to the query is direct. Every value is checked against a fixed
 * domain here, because the gateway answers any bad input with a bare
 * `invalid_parameter` and the page could not say which control was wrong.
 */

export const ETF_PAGE_SIZE = 20;

/** Column groups shown beside the five fixed columns. */
export const ETF_VIEW_TABS = ["weights", "rulebooks", "fund", "basket"] as const;
export type EtfViewTab = (typeof ETF_VIEW_TABS)[number];

export const ETF_STATUS_VALUES = ["HALAL", "DOUBTFUL", "NOT HALAL", "UNQUALIFIED"] as const;
export const ETF_VENUE_VALUES = ["US", "GB", "CA"] as const;
/** Domicile is open upstream; these are the common ones offered as options. */
export const ETF_DOMICILE_VALUES = ["US", "IE", "CA", "LU", "FR", "SE"] as const;
export const ETF_COVERAGE_BANDS = ["50", "80", "90", "95", "99"] as const;
export const ETF_EXPENSE_BANDS = ["0.1", "0.2", "0.5", "0.75", "1"] as const;
export const ETF_AUM_BANDS = ["10000000", "100000000", "1000000000", "10000000000"] as const;

export interface EtfFilterValues {
  status: string;
  /** Which rulebook `status` filters. AAOIFI = the Akinda fund screen. */
  methodology: EtfMethodology;
  venue: string;
  domicile: string;
  coverage: string;
  expense: string;
  aum: string;
  issuer: string;
  q: string;
}

export interface EtfSort {
  key: EtfSortKey;
  order: EtfSortOrder;
}

export const DEFAULT_ETF_SORT: EtfSort = { key: "aum", order: "desc" };

export const EMPTY_ETF_FILTERS: EtfFilterValues = {
  status: "",
  methodology: "aaoifi",
  venue: "",
  domicile: "",
  coverage: "",
  expense: "",
  aum: "",
  issuer: "",
  q: "",
};

const MAX_TEXT = 100;

const oneOf = <T extends string>(values: readonly T[], value: unknown): T | "" =>
  typeof value === "string" && (values as readonly string[]).includes(value) ? (value as T) : "";

const text = (value: unknown): string =>
  typeof value === "string" ? value.trim().slice(0, MAX_TEXT) : "";

/** Any object → valid filter values; unknown or out-of-domain values are dropped. */
export const sanitizeEtfFilters = (raw: Partial<Record<keyof EtfFilterValues, unknown>>): EtfFilterValues => ({
  status: oneOf(ETF_STATUS_VALUES, raw.status),
  methodology: oneOf(ETF_METHODOLOGIES, raw.methodology) || "aaoifi",
  venue: oneOf(ETF_VENUE_VALUES, raw.venue),
  domicile: oneOf(ETF_DOMICILE_VALUES, raw.domicile),
  coverage: oneOf(ETF_COVERAGE_BANDS, raw.coverage),
  expense: oneOf(ETF_EXPENSE_BANDS, raw.expense),
  aum: oneOf(ETF_AUM_BANDS, raw.aum),
  issuer: text(raw.issuer),
  q: text(raw.q),
});

export const sanitizeEtfSort = (key: unknown, order: unknown): EtfSort => {
  const sortKey = oneOf(ETF_SORT_KEYS, key);
  if (!sortKey) return DEFAULT_ETF_SORT;
  return { key: sortKey, order: order === "asc" ? "asc" : "desc" };
};

export const hasActiveEtfFilters = (filters: EtfFilterValues): boolean =>
  Boolean(
    filters.status ||
      filters.venue ||
      filters.domicile ||
      filters.coverage ||
      filters.expense ||
      filters.aum ||
      filters.issuer ||
      filters.q,
  ) || filters.methodology !== "aaoifi";

export const isDefaultEtfSort = (sort: EtfSort): boolean =>
  sort.key === DEFAULT_ETF_SORT.key && sort.order === DEFAULT_ETF_SORT.order;

/** One screener page in our proxy's terms (page/limit; the proxy maps to offset). */
export const etfFiltersToQuery = (
  filters: EtfFilterValues,
  sort: EtfSort,
  page: number,
  limit: number = ETF_PAGE_SIZE,
): EtfScreenerQuery => {
  const query: EtfScreenerQuery = { page, limit, sort: sort.key, order: sort.order };
  if (filters.status) {
    query.status = filters.status;
    // The rulebook only matters together with a status.
    query.methodology = filters.methodology;
  }
  if (filters.venue) query.venue = filters.venue;
  if (filters.domicile) query.domicile = filters.domicile;
  if (filters.coverage) query.min_coverage = filters.coverage;
  if (filters.expense) query.max_expense_ratio = filters.expense;
  if (filters.aum) query.min_aum = filters.aum;
  if (filters.issuer) query.issuer = filters.issuer;
  if (filters.q) query.q = filters.q;
  return query;
};

// ---- URL state (share links, server-rendered first paint) ----------------

export interface EtfUrlState {
  filters: EtfFilterValues;
  sort: EtfSort;
  page: number;
}

const URL_KEYS: (keyof EtfFilterValues)[] = [
  "status",
  "methodology",
  "venue",
  "domicile",
  "coverage",
  "expense",
  "aum",
  "issuer",
  "q",
];

export const parseEtfSearchParams = (params: URLSearchParams): EtfUrlState | null => {
  const hasAny =
    params.has("sort") || params.has("page") || URL_KEYS.some((key) => params.has(key));
  if (!hasAny) return null;
  const raw: Partial<Record<keyof EtfFilterValues, unknown>> = {};
  for (const key of URL_KEYS) raw[key] = params.get(key) ?? "";
  const pageRaw = Number(params.get("page"));
  return {
    filters: sanitizeEtfFilters(raw),
    sort: sanitizeEtfSort(params.get("sort"), params.get("order")),
    page: Number.isFinite(pageRaw) && pageRaw >= 1 ? Math.min(1000, Math.floor(pageRaw)) : 1,
  };
};

export const etfStateToSearchParams = (state: EtfUrlState): URLSearchParams => {
  const params = new URLSearchParams();
  for (const key of URL_KEYS) {
    const value = state.filters[key];
    if (!value || (key === "methodology" && value === "aaoifi")) continue;
    params.set(key, value);
  }
  if (!isDefaultEtfSort(state.sort)) {
    params.set("sort", state.sort.key);
    params.set("order", state.sort.order);
  }
  if (state.page > 1) params.set("page", String(state.page));
  return params;
};

// ---- Session state (restore where this tab left off) ----------------------

const SESSION_KEY = "akinda.etfScreener.session";

export interface EtfSessionState extends EtfUrlState {
  draftFilters: EtfFilterValues;
}

export const loadEtfSessionState = (): EtfSessionState | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<EtfSessionState>;
    if (!parsed.filters || typeof parsed.filters !== "object") return null;
    const filters = sanitizeEtfFilters(parsed.filters);
    const draftFilters =
      parsed.draftFilters && typeof parsed.draftFilters === "object"
        ? sanitizeEtfFilters(parsed.draftFilters)
        : filters;
    const sort = sanitizeEtfSort(parsed.sort?.key, parsed.sort?.order);
    const pageRaw = Number(parsed.page);
    const page = Number.isFinite(pageRaw) && pageRaw >= 1 ? Math.floor(pageRaw) : 1;
    return { filters, draftFilters, sort, page };
  } catch {
    return null;
  }
};

export const saveEtfSessionState = (state: EtfSessionState): void => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
  } catch {
    // Private mode / quota: the page works without it.
  }
};

export const clearEtfSessionState = (): void => {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
};
